import { doc, getDoc } from "firebase/firestore";
import { CONFIG_DOC, COL, type Accion } from "@zociety/core";
import { app, db } from "./firebase";
import { usarDestino, type DestinoAnalitica } from "./analitica";

/**
 * El destino real: Google Analytics.
 *
 * Es la única pieza que cambia respecto de la versión anterior. El catálogo, la
 * validación y los puntos donde se emite ya estaban; esto solo conecta la
 * salida. Era el objetivo de haber construido el vocabulario primero.
 *
 * ## Nace apagada y se enciende desde la base
 *
 * `configuraciones/analitica.activa` manda, y por omisión es `false`. No es una
 * variable de compilación porque **encenderla no es una decisión técnica**:
 * registrar el comportamiento de personas exige aviso de privacidad por la
 * LFPDPPP y eso lo aprueba la dueña del negocio. Un interruptor en la base pone
 * esa decisión donde corresponde, deja constancia de cuándo se tomó y se puede
 * revertir sin desplegar.
 *
 * Mientras esté apagada **no se inicializa Analytics siquiera**. Inicializarlo y
 * luego no emitir no serviría: en cuanto arranca empieza a recoger vistas de
 * página por su cuenta.
 *
 * ## No se manda `user_id`
 *
 * Es la decisión de privacidad que sostiene todo lo demás. Sin él, lo que llega
 * a Google es seudónimo: se puede contar cuánta gente inició un pago y cuánta lo
 * terminó —que es lo que el negocio pregunta— sin construir el historial de
 * conducta de una persona con nombre.
 *
 * Añadirlo sube el listón legal de golpe: pasaría a hacer falta consentimiento
 * expreso y una forma de ejercer derechos ARCO sobre esos datos. Si algún día se
 * quiere, es otra conversación y otro trabajo, no una línea más aquí.
 */

/** Por qué la analítica quedó encendida o apagada. Se devuelve para poder verlo. */
export type ResultadoEncendido =
  | "encendida"
  | "apagada-por-configuracion"
  | "sin-measurement-id"
  | "navegador-no-soportado"
  | "fallo";

async function estaAutorizada(): Promise<boolean> {
  try {
    const snap = await getDoc(doc(db, COL.CONFIGURACIONES, CONFIG_DOC.ANALITICA));
    return snap.exists() && snap.get("activa") === true;
  } catch {
    /*
     * Si no se puede leer el permiso, no se enciende.
     *
     * El silencio se interpreta como «no», nunca como «adelante»: un fallo de
     * red no puede acabar recogiendo datos que nadie autorizó.
     */
    return false;
  }
}

export async function encenderAnalitica(): Promise<ResultadoEncendido> {
  try {
    if (!(await estaAutorizada())) return "apagada-por-configuracion";

    /* Sin identificador de medición no hay a dónde mandar nada. */
    if (!app.options.measurementId) return "sin-measurement-id";

    /*
     * `firebase/analytics` se importa aquí dentro y no arriba.
     *
     * Así su código no entra en el paquete inicial de quien nunca lo va a usar,
     * que hoy son todos. Es la diferencia entre cargarlo siempre y cargarlo
     * cuando de verdad se enciende.
     */
    const { getAnalytics, isSupported, logEvent, setUserId } = await import("firebase/analytics");

    /* Devuelve falso donde el SDK no puede funcionar; ahí no se insiste. */
    if (!(await isSupported())) return "navegador-no-soportado";

    const ga = getAnalytics(app);

    /*
     * Explícito, aunque sea el valor por omisión.
     *
     * Que nadie tenga que deducir de la ausencia de una llamada que no se está
     * mandando el identificador de la persona. Aquí está dicho.
     */
    setUserId(ga, null);

    /*
     * El molde es necesario y conviene decir por qué.
     *
     * `logEvent` tiene una sobrecarga por cada nombre estándar —`purchase`,
     * `add_to_cart`…— cada una con sus parámetros, más una final para nombres
     * propios que **excluye** los estándar. Nuestro catálogo mezcla los dos, así
     * que ninguna sobrecarga acepta la unión entera.
     *
     * Se pierde la comprobación de tipos del SDK y no se pierde nada más: lo que
     * llega aquí ya pasó por `prepararAccion`, que valida nombres, claves, tipos
     * y límites con sus propias pruebas.
     */
    const emitir = logEvent as unknown as (
      a: typeof ga,
      nombre: string,
      parametros?: Record<string, unknown>
    ) => void;

    const destino: DestinoAnalitica = {
      nombre: "ga4",
      enviar: (accion: Accion) =>
        emitir(ga, accion.nombre, {
          ...accion.parametros,
          ...(accion.articulos?.length ? { items: accion.articulos } : {}),
        }),
    };

    usarDestino(destino);
    return "encendida";
  } catch {
    /* Una medición jamás puede impedir que la aplicación arranque. */
    return "fallo";
  }
}
